import { useCallback, useMemo } from "react";
import { useI18n, t } from "@bookingcom/lingojs-react";
import useSearchFiltersController from "../../useSearchFiltersController";
import useFormatPrice from "../../../../../hooks/useFormatPrice";

export function useWeekendTopFiltersController() {
  const i18n = useI18n();
  const { formatPrice } = useFormatPrice();
  const {
    stops,
    cabinBaggage,
    eveningReturn,
    budgetPerAdult,
    minPricePerAdult,
    morningDeparture,
    resetWeekendTopFilters
  } = useSearchFiltersController();

  const maxPrice = useMemo(() => {
    if (!minPricePerAdult?.units) return undefined;
    if (!budgetPerAdult.options?.min?.units) return undefined;
    if (!budgetPerAdult.options?.max?.units) return undefined;
    const targetValue = Math.ceil(minPricePerAdult.units * 1.28); // 128% of min price
    if (targetValue > budgetPerAdult.options.max.units) return undefined;
    if (targetValue < budgetPerAdult.options.min.units) return undefined;
    const { currencyCode } = budgetPerAdult.options.min;
    return { units: targetValue, nanos: 0, currencyCode };
  }, [budgetPerAdult, minPricePerAdult]);

  const onChange = useCallback(
    ({ checked, name }: { checked: boolean; name: string }) => {
      switch (name) {
        case "direct-flights":
          return checked ? stops.set(0) : stops.reset();
        case "morning-departure-flight":
          return checked ? morningDeparture.set() : morningDeparture.reset();
        case "evening-return-flight":
          return checked ? eveningReturn.set() : eveningReturn.reset();
        case "included-cabin-bag":
          return checked ? cabinBaggage.set() : cabinBaggage.reset();
        case "budget-friendly":
          return maxPrice && checked ? budgetPerAdult.set(maxPrice.units) : budgetPerAdult.reset();
        default:
          return;
      }
    },
    [stops, morningDeparture, eveningReturn, cabinBaggage, budgetPerAdult, maxPrice]
  );

  const onReset = useCallback(() => {
    resetWeekendTopFilters();
  }, [resetWeekendTopFilters]);

  const directFlights = useMemo(() => {
    const isAvailable = !!stops?.options?.find?.((o) => o?.value === 0)?.count;
    const checked = stops.value === 0;
    if (!isAvailable && !checked) return undefined;
    return {
      checked,
      onChange,
      name: "direct-flights",
      label: i18n.trans(t("flights_filters_weekenders_direct_flights"))
    };
  }, [stops, onChange, i18n]);

  const morningDepartureflights = useMemo(() => {
    const isAvailable = !!morningDeparture.count;
    const checked = morningDeparture.checked;
    if (!isAvailable && !checked) return undefined;
    return {
      checked,
      onChange,
      name: "morning-departure-flight",
      label: i18n.trans(t("flights_filters_weekenders_morning_departure"))
    };
  }, [morningDeparture, onChange, i18n]);

  const budgetFriendlyFlights = useMemo(() => {
    if (!maxPrice) return undefined;
    const notSame = budgetPerAdult.options.max.units !== budgetPerAdult.options.min.units;
    const isAvailable =
      notSame &&
      !!(budgetPerAdult.options.min.units !== budgetPerAdult.options.max.units) &&
      budgetPerAdult.options.min.units < maxPrice.units &&
      budgetPerAdult.options.max.units > maxPrice.units;
    const checked = budgetPerAdult.value === maxPrice.units && notSame;
    if (!isAvailable && !checked) return undefined;
    const price = formatPrice(maxPrice);
    return {
      checked,
      onChange,
      name: "budget-friendly",
      label: i18n.trans(t("flights_filters_weekenders_budget_price", { variables: { max_price: price } }))
    };
  }, [budgetPerAdult, maxPrice, onChange, i18n, formatPrice]);

  const cabinBaggageIncludedFlights = useMemo(() => {
    const isAvailable = !!cabinBaggage.count;
    const checked = cabinBaggage.checked;
    if (!isAvailable && !checked) return undefined;
    return {
      checked,
      onChange,
      name: "included-cabin-bag",
      label: i18n.trans(t("flights_filters_weekenders_cabin_included"))
    };
  }, [i18n, onChange, cabinBaggage]);

  const eveningReturnFlights = useMemo(() => {
    const isAvailable = !!eveningReturn.count;
    const checked = eveningReturn.checked;
    if (!isAvailable && !checked) return undefined;
    return {
      checked,
      onChange,
      name: "evening-return-flight",
      label: i18n.trans(t("flights_filters_weekenders_evening_return"))
    };
  }, [i18n, onChange, eveningReturn]);

  const restText = useMemo(() => i18n.trans(t("flights_filters_weekenders_reset")), [i18n]);

  const title = useMemo(() => i18n.trans(t("flights_filters_weekenders_header")), [i18n]);

  return {
    title,
    onReset,
    restText,
    directFlights,
    eveningReturnFlights,
    budgetFriendlyFlights,
    morningDepartureflights,
    cabinBaggageIncludedFlights
  };
}
